import { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import { LogEmptyState } from './LogEmptyState';
import { JsonSyntax } from './JsonSyntax';
import { summarizeJson } from './log-viewer-utils';

export function JsonlViewer({ lines, filter }: { lines: string[]; filter: string }) {
  const entries = useMemo(() => {
    return lines
      .filter((l) => l.trim())
      .map((line, i) => {
        try {
          return {
            idx: i,
            parsed: JSON.parse(line) as Record<string, unknown>,
            raw: line,
            error: false,
          };
        } catch {
          return { idx: i, parsed: null, raw: line, error: true };
        }
      });
  }, [lines]);

  const filtered = useMemo(() => {
    if (!filter) return entries;
    const lower = filter.toLowerCase();
    return entries.filter((e) => e.raw.toLowerCase().includes(lower));
  }, [entries, filter]);

  return (
    <div className="space-y-1">
      {filtered.map((entry) => (
        <JsonEntry key={entry.idx} entry={entry} />
      ))}
      {filtered.length === 0 && <LogEmptyState>No matching entries</LogEmptyState>}
    </div>
  );
}

function JsonEntry({
  entry,
}: {
  entry: { idx: number; parsed: Record<string, unknown> | null; raw: string; error: boolean };
}) {
  const [expanded, setExpanded] = useState(false);

  if (entry.error || !entry.parsed) {
    return <div className="font-mono text-2xs text-muted-foreground px-2 py-1">{entry.raw}</div>;
  }

  const obj = entry.parsed;
  const seq = obj.sequence as number | undefined;
  const eventType = obj.event_type as string | undefined;

  return (
    <div className="group rounded-md border border-border/60 bg-card/50 hover:border-border transition-colors">
      <button
        type="button"
        className="flex items-center gap-2 w-full text-left px-2.5 py-1.5"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        {seq != null && (
          <span className="font-mono text-3xs text-muted-foreground w-6 text-right shrink-0">
            #{seq}
          </span>
        )}
        {eventType && (
          <Badge variant="outline" className="text-3xs font-mono font-medium px-1.5 py-0 shrink-0">
            {eventType}
          </Badge>
        )}
        <span className="font-mono text-3xs text-muted-foreground truncate">
          {summarizeJson(obj)}
        </span>
      </button>
      <AnimatedCollapse open={expanded}>
        <div
          data-testid="expanded-content"
          className="px-3 pb-2.5 pt-0.5 border-t border-border/40"
        >
          <pre className="text-2xs font-mono leading-relaxed whitespace-pre-wrap break-words">
            <JsonSyntax value={obj} />
          </pre>
        </div>
      </AnimatedCollapse>
    </div>
  );
}
